import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { Camera, Save } from "lucide-react";
import { useStatus } from "../../popup/NotificationContext";
import { setUser } from "../../store/authSlice";
import { getOrCreateClientDeviceId } from "../../utils/deviceId";

import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  IconButton,
  Stack,
  Divider,
  Chip,
  Switch,
} from "@mui/material";

const formatRoleLabel = (value = "") =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const resolveImageUrl = (rawUrl, { baseUrl = "", fallbackUrl = "" } = {}) => {
  const value = String(rawUrl || "").trim();
  if (!value) return fallbackUrl;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }
  return `${baseUrl}${value}`;
};

const GeneralSetting = () => {
  const dispatch = useDispatch();
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth.user);
  const currentRole = String(currentUser?.role || "").trim().toLowerCase();
  const showBarcodeScannerSection = currentRole !== "user";
  const effectiveBarcodeOwner = currentUser?.effectiveBarcodeScannerOwner || null;
  const directBillingOwner = currentUser?.directBillingOwner || null;
  const barcodePaymentVisibilityBlockedBy = currentUser?.barcodePaymentVisibilityBlockedBy || null;
  const isOwnBarcodeActive =
    Boolean(currentUser?.barcodeScannerImage) &&
    String(effectiveBarcodeOwner?._id || "") === String(currentUser?._id || "");

  // --- Form States ---
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNo: "",
    timezone: "Asia/Kolkata",
    speedUnit: "km/h",
    distanceUnit: "km",
    altitudeUnit: "m",
    volumeUnit: "Liter",
    supportContact: "",
    supportEmail: "info@qylmox.com",
    barcodePaymentEnabled: true,
  });

  const [profilePictureDisplayUrl, setProfilePictureDisplayUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [barcodeScannerDisplayUrl, setBarcodeScannerDisplayUrl] = useState("");
  const [selectedBarcodeScannerFile, setSelectedBarcodeScannerFile] = useState(null);
  const fileInputRef = useRef(null);
  const barcodeScannerInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const placeholderImage = "/profile-placeholder.png";
  const staticFilesBaseUrl =
    backendUrl && backendUrl.endsWith("/api")
      ? backendUrl.slice(0, -4)
      : backendUrl || "";

  // --- Initialize Data from Redux ---
  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        username: currentUser.username || "",
        email: currentUser.email || "",
        mobileNo: currentUser.mobileNo || "",
        barcodePaymentEnabled: currentUser.barcodePaymentEnabled !== false,
        // units mapping (agar backend me saved ho) yahin add kar sakte ho (logic same)
      }));

      const picUrl = `${resolveImageUrl(currentUser.profilePicture, {
        baseUrl: staticFilesBaseUrl,
        fallbackUrl: `${staticFilesBaseUrl}${placeholderImage}`,
      })}?t=${Date.now()}`;
      setProfilePictureDisplayUrl(picUrl);

      const barcodeSourceUrl =
        currentUser.barcodeScannerImage ||
        currentUser.effectiveBarcodeScannerImage ||
        "";
      const barcodeUrl = barcodeSourceUrl
        ? `${resolveImageUrl(barcodeSourceUrl, {
            baseUrl: staticFilesBaseUrl,
            fallbackUrl: "",
          })}?t=${Date.now()}`
        : "";
      setBarcodeScannerDisplayUrl(barcodeUrl);
    }
  }, [currentUser, staticFilesBaseUrl]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return showStatus("error", "Only image files allowed.");
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePictureDisplayUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBarcodeScannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return showStatus("error", "Only image files allowed.");
    }
    setSelectedBarcodeScannerFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setBarcodeScannerDisplayUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const deviceId = getOrCreateClientDeviceId();

      // 1) Update Text Data
      const response = await axios.put(
        `${backendUrl}/users/profile`,
        {
          username: formData.username,
          email: formData.email,
          mobileNo: formData.mobileNo,
          barcodePaymentEnabled: Boolean(formData.barcodePaymentEnabled),
        },
        { headers: { "x-auth-token": token, "x-device-id": deviceId } }
      );

      // 2) Update Profile Picture (If selected)
      if (selectedFile) {
        const uploadData = new FormData();
        uploadData.append("profilePicture", selectedFile);

        const picResponse = await axios.post(
          `${backendUrl}/users/profile/picture`,
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-auth-token": token,
              "x-device-id": deviceId,
            },
          }
        );

        dispatch(setUser(picResponse.data.user));
      }

      if (selectedBarcodeScannerFile) {
        const uploadData = new FormData();
        uploadData.append("barcodeScannerImage", selectedBarcodeScannerFile);

        const barcodeResponse = await axios.post(
          `${backendUrl}/users/profile/barcode-scanner`,
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-auth-token": token,
              "x-device-id": deviceId,
            },
          }
        );

        dispatch(setUser(barcodeResponse.data.user));
      }

      if (!selectedFile && !selectedBarcodeScannerFile) {
        dispatch(setUser(response.data.user));
      }

      showStatus("success", "Profile updated successfully!");
      setSelectedFile(null);
      setSelectedBarcodeScannerFile(null);
    } catch (err) {
      showStatus("error", err.response?.data?.msg || err.response?.data?.message || "Update failed.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header */}
        <Stack spacing={0.5} mb={2}>
          <Typography variant="h6" fontWeight={800}>
            General Setting
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update profile, units & support information
          </Typography>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleUpdateProfile}>
          {/* Avatar Upload */}
          <Stack alignItems="center" spacing={1.25} mb={3}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={profilePictureDisplayUrl}
                alt="Profile"
                sx={{
                  width: 120,
                  height: 120,
                  border: "3px solid",
                  borderColor: "primary.main",
                }}
              />

              {/* camera overlay */}
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  bgcolor: "rgba(0,0,0,0.55)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  width: 40,
                  height: 40,
                }}
              >
                <Camera size={18} color="#fff" />
              </IconButton>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                hidden
                accept="image/*"
              />
            </Box>

            {selectedFile && (
              <Typography variant="caption" sx={{ color: "primary.main" }}>
                Image selected. Click SAVE to upload.
              </Typography>
            )}
          </Stack>

          {showBarcodeScannerSection && (
            <Stack alignItems="center" spacing={1.25} mb={3}>
              <Typography variant="subtitle1" fontWeight={700}>
                Barcode Scanner
              </Typography>
              <Box sx={{ position: "relative" }}>
                <Avatar
                  variant="rounded"
                  src={barcodeScannerDisplayUrl || undefined}
                  alt="Barcode Scanner"
                  sx={{
                    width: 180,
                    height: 120,
                    border: "2px dashed",
                    borderColor: "primary.main",
                    bgcolor: "grey.50",
                  }}
                />

                <IconButton
                  onClick={() => barcodeScannerInputRef.current?.click()}
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    right: 6,
                    bgcolor: "rgba(0,0,0,0.55)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    width: 40,
                    height: 40,
                  }}
                >
                  <Camera size={18} color="#fff" />
                </IconButton>

                <input
                  type="file"
                  ref={barcodeScannerInputRef}
                  onChange={handleBarcodeScannerFileChange}
                  hidden
                  accept="image/*"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" align="center">
                Barcode scanner image/profile yahin save hoga. Upload ke baad profile me permanent rahega.
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(formData.barcodePaymentEnabled)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        barcodePaymentEnabled: e.target.checked,
                      }))
                    }
                  />
                )}
                label={
                  formData.barcodePaymentEnabled
                    ? "Barcode payment enabled"
                    : "Barcode payment disabled"
                }
              />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                {barcodePaymentVisibilityBlockedBy?._id ? (
                  <Chip
                    size="small"
                    color="default"
                    label={`Hidden by ${barcodePaymentVisibilityBlockedBy.username || "owner"}`}
                  />
                ) : isOwnBarcodeActive ? (
                  <Chip
                    size="small"
                    color="success"
                    label="Your barcode is active"
                  />
                ) : effectiveBarcodeOwner?._id ? (
                  <Chip
                    size="small"
                    color="info"
                    label={`Using ${effectiveBarcodeOwner.username || "owner"} barcode`}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="warning"
                    label="No active barcode found"
                  />
                )}

                {effectiveBarcodeOwner?.role ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={formatRoleLabel(effectiveBarcodeOwner.role)}
                  />
                ) : null}
              </Stack>

              <Typography variant="caption" color="text.secondary" align="center">
                {barcodePaymentVisibilityBlockedBy?._id
                  ? `${barcodePaymentVisibilityBlockedBy.username || "Owner"} ne barcode payment disabled kiya hua hai, isliye app me barcode show nahi hoga.`
                  : isOwnBarcodeActive
                  ? "Ye barcode aapke aur aapke neeche wale roles ke renewals/payment flow me use hoga."
                  : effectiveBarcodeOwner?._id
                    ? `Agar aap apna barcode upload nahi karte, system ${effectiveBarcodeOwner.username || "owner"} (${formatRoleLabel(
                        effectiveBarcodeOwner.role
                      )}) ka barcode use karega.`
                    : directBillingOwner?._id
                      ? `Abhi active barcode nahi mila. Expected owner: ${directBillingOwner.username || "owner"} (${formatRoleLabel(
                          directBillingOwner.role
                        )}).`
                      : "Abhi tak koi effective barcode owner resolve nahi hua."}
              </Typography>

              {selectedBarcodeScannerFile && (
                <Typography variant="caption" sx={{ color: "primary.main" }}>
                  Barcode scanner image selected. Click SAVE to upload.
                </Typography>
              )}
            </Stack>
          )}

          {/* Form Grid */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="User Name"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Speed Unit</InputLabel>
                <Select
                  label="Speed Unit"
                  name="speedUnit"
                  value={formData.speedUnit}
                  onChange={handleInputChange}
                >
                  <MenuItem value="km/h">km/h</MenuItem>
                  <MenuItem value="mph">mph</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Distance Unit</InputLabel>
                <Select
                  label="Distance Unit"
                  name="distanceUnit"
                  value={formData.distanceUnit}
                  onChange={handleInputChange}
                >
                  <MenuItem value="km">km</MenuItem>
                  <MenuItem value="mi">miles</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Altitude Unit</InputLabel>
                <Select
                  label="Altitude Unit"
                  name="altitudeUnit"
                  value={formData.altitudeUnit}
                  onChange={handleInputChange}
                >
                  <MenuItem value="m">meters (m)</MenuItem>
                  <MenuItem value="ft">feet (ft)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Volume Unit</InputLabel>
                <Select
                  label="Volume Unit"
                  name="volumeUnit"
                  value={formData.volumeUnit}
                  onChange={handleInputChange}
                >
                  <MenuItem value="Liter">Liter</MenuItem>
                  <MenuItem value="Gallon">Gallon</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Keeping names EXACTLY like your original logic */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Support Contact Number"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleInputChange}
                placeholder="Enter contact number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Support Email Address"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          {/* Save Button */}
          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<Save size={18} />}
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Save Changes
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default GeneralSetting;
